import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Trash2, UserCog, Search } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';

export default function HRManagers() {
  const [hrUsers, setHrUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentEmployerId, setCurrentEmployerId] = useState(null);
  const { confirmDialogProps, openConfirm } = useConfirmDialog();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      setCurrentEmployerId(user.employer_id);

      const users = await api.entities.User.list();
      const hrManagers = users.filter(u => u.user_role === 'employer_hr' && u.employer_id === user.employer_id);
      
      setHrUsers(hrManagers);
      setAllUsers(users);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredHRUsers = hrUsers.filter(user =>
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openModal = (user = null) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleSave = async (email) => {
    try {
      const existingUsers = allUsers.filter(u => u.email === email);
      
      if (existingUsers.length > 0) {
        // Update existing user
        await api.entities.User.update(existingUsers[0].id, {
          user_role: 'employer_hr',
          employer_id: currentEmployerId
        });
        toast.success('User updated to HR Manager role');
      } else {
        // Create new user
        await api.entities.User.create({
          email,
          user_role: 'employer_hr',
          employer_id: currentEmployerId
        });
        toast.success('HR Manager added successfully');
      }
      
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save HR Manager');
    }
  };

  const removeHrManager = async (user) => {
    try {
      await api.entities.User.update(user.id, {
        user_role: 'employee'
      });
      toast.success('HR Manager removed');
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to remove HR Manager');
    }
  };

  const confirmRemove = (user) => {
    openConfirm({
      title: 'Remove HR access?',
      description: `${user.email} will lose access to HR tools but keep their employee account. You can re-grant access later from this page.`,
      confirmLabel: 'Remove HR access',
      isDestructive: true,
      onConfirm: () => removeHrManager(user)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">HR Managers</h1>
          <p className="text-slate-500 mt-1">
            Manage users with HR access to employee data
          </p>
        </div>
        <Button 
          onClick={() => openModal()}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add HR Manager
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search HR managers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredHRUsers.map((user) => (
          <Card key={user.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white font-bold text-lg">
                  {user.email?.[0]?.toUpperCase() || 'H'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{user.email}</p>
                  <Badge className="mt-2 bg-purple-100 text-purple-700">
                    <UserCog className="w-3 h-3 mr-1" />
                    HR Manager
                  </Badge>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-red-600 hover:bg-red-50"
                  onClick={() => confirmRemove(user)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredHRUsers.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-500">
            No HR managers found
          </div>
        )}
      </div>

      <AddHRManagerModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
      />
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}

function AddHRManagerModal({ open, onClose, onSave }) {
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }
    onSave(email);
    setEmail('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add HR Manager</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Email Address</Label>
            <Input
              type="email"
              placeholder="manager@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="text-sm text-slate-500">
              Enter the email address of the person you want to grant HR access
            </p>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600">
              Add HR Manager
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}